import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiBaseUrl, getAuthHeaders } from '@/services/api';
import type { ServicesApiAsset } from '@/services/api/services-api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { parseAsset, assetKeys, type Asset } from './entity';

/**
 * Multipart file upload for assets.
 *
 * The upload endpoint (`POST /v1/environments/:id/assets/upload`) uses
 * `multipart/form-data`, which ts-rest doesn't support. This module
 * provides a standalone function and hook that mirror the mutation
 * patterns in `mutations.ts` but use raw `fetch` for the request.
 */

/* ------------------------------------------------------------------------- */
/* uploadAsset — raw fetch with FormData                                      */
/* ------------------------------------------------------------------------- */

export interface UploadAssetInput {
  workspaceId: number;
  file: File;
  parentFolderId?: string | null;
}

/** @public */
export async function uploadAsset(input: UploadAssetInput): Promise<Asset> {
  const formData = new FormData();
  formData.append('file', input.file, input.file.name);

  if (input.parentFolderId != null) {
    formData.append('parent_folder_id', String(input.parentFolderId));
  }

  const apiUrl = getApiBaseUrl();
  const response = await fetch(`${apiUrl}/v1/environments/${input.workspaceId}/assets/upload`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
    headers: getAuthHeaders(),
  });

  const body = (await response.json()) as { asset?: ServicesApiAsset; errors?: Array<{ detail: string }> };

  if (!response.ok || body.asset == null) {
    const message = body.errors?.[0]?.detail ?? `Upload failed with status ${response.status}`;
    throw new Error(message);
  }

  return parseAsset(body.asset);
}

/* ------------------------------------------------------------------------- */
/* useUploadAsset hook                                                        */
/* ------------------------------------------------------------------------- */

type UseUploadAssetVariables = Omit<UploadAssetInput, 'workspaceId'>;

interface UseUploadAssetOptions {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<Asset, UseUploadAssetVariables>;
}

/** @public */
export const useUploadAsset = ({ workspaceId: overrideWorkspaceId, mutationConfig }: UseUploadAssetOptions = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: UseUploadAssetVariables) => uploadAsset({ workspaceId, ...variables }),
    onSuccess: (uploaded, ...callbackArgs) => {
      qc.setQueryData(assetKeys.detail(workspaceId, uploaded.id), uploaded);
      void qc.invalidateQueries({ queryKey: assetKeys.lists(workspaceId) });
      onSuccess?.(uploaded, ...callbackArgs);
    },
  });
};
